import axios, { AxiosInstance } from 'axios';
import getConfig from 'next/config';
import { getSession } from 'next-auth/react';
import { createTokenRefreshInterceptor } from '@dist-marketplace-frontend/services';

const LOCALHOST = 'http://localhost:3000';

const {
  publicRuntimeConfig: {
    APP_VERSION,
    API_DONEDEAL_MAIN,
    API_DONEDEAL_SERVICES,
    API_DONEDEAL_AUTOCOMPLETE,
    API_INTERNAL,
    CLIENT_DONEDEAL_MAIN,
    INTEGRATION_TESTING,
    ENVIRONMENT_NAME,
    MOTOR_FINANCE_GATEWAY_BASE_URL,
  },
  // This is a valid case of using getConfig at the module scope level
  // eslint-disable-next-line local-rules/no-top-level-getconfig
} = getConfig();

export const baseHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  brand: 'donedeal',
  platform: 'web',
  version: APP_VERSION ?? '0',
};

/**
 * This function will swap a server-side gateway domain for the client hostname when axios gateway is used by the client
 * Will also set the correct top level domain (eg. co.uk)
 * @param serverDomain domain to be used on the server
 * @returns baseURL
 */
const swapOriginForClient = (serverDomain: string, isFEServer = false) => {
  if (INTEGRATION_TESTING) {
    return serverDomain;
  }

  if (typeof window !== 'undefined') {
    if (ENVIRONMENT_NAME === 'dev' || ENVIRONMENT_NAME === 'preview') {
      if (isFEServer && window?.location?.origin === LOCALHOST) {
        return LOCALHOST;
      }

      return CLIENT_DONEDEAL_MAIN;
    } else {
      return window.location.origin;
    }
  }
  return serverDomain;
};

/**
 * @deprecated Please use donedealServicesGateway to ensure that the correct
 * gateway is used based on whether the request is made server-side or client-side
 */
export const donedealGateway = axios.create({
  baseURL: swapOriginForClient(API_DONEDEAL_MAIN),
  headers: { ...baseHeaders },
});

export const donedealAutoCompleteGateway = axios.create({
  baseURL: API_DONEDEAL_AUTOCOMPLETE,
  headers: { ...baseHeaders },
});

export const donedealServicesGateway = axios.create({
  baseURL: swapOriginForClient(API_DONEDEAL_SERVICES),
  headers: { ...baseHeaders },
});

/**
 * This gateway should be used for NextJS server routes - the `api` is already prepended
 */
export const frontendServerGateway = axios.create({
  baseURL: `${swapOriginForClient(API_INTERNAL, true)}/api`,
  headers: { ...baseHeaders },
});

export const donedealServerGateway = axios.create({
  baseURL: swapOriginForClient(API_DONEDEAL_MAIN),
  headers: { ...baseHeaders },
});

export const motorFinanceGateway = axios.create({
  baseURL: MOTOR_FINANCE_GATEWAY_BASE_URL,
  headers: baseHeaders,
});

// Track the interceptor id for each gateway to allow for eventual cleanup
const interceptorIdMap = new Map<AxiosInstance, number>();

export function setupAuthInterceptor({
  onSessionRefreshSuccess,
  onSessionRefreshError,
}: {
  onSessionRefreshSuccess: () => Promise<void>;
  onSessionRefreshError: () => Promise<void>;
}): void {
  if (typeof window === 'undefined') {
    return;
  }

  const {
    publicRuntimeConfig: {
      FEATURE_AXIOS_TOKEN_REFRESH_INTERCEPTOR,
      API_CLIENT_TIMEOUT,
    },
  } = getConfig();

  const interceptor = createTokenRefreshInterceptor({
    getSession,
    onSessionRefreshSuccess,
    onSessionRefreshError,
    enabled: FEATURE_AXIOS_TOKEN_REFRESH_INTERCEPTOR,
    refreshTimeoutMs: Number(API_CLIENT_TIMEOUT),
  });

  const gateways = [
    donedealGateway,
    donedealServicesGateway,
    motorFinanceGateway,
  ];

  gateways.forEach((gateway) => {
    const existingInterceptorId = interceptorIdMap.get(gateway);

    // Clean up any existing interceptors
    if (existingInterceptorId !== undefined) {
      gateway.interceptors.request.eject(existingInterceptorId);
      interceptorIdMap.delete(gateway);
    }

    // Add the new interceptor
    interceptorIdMap.set(
      gateway,
      gateway.interceptors.request.use(interceptor),
    );
  });
}
